"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Users,
  Target,
  Compass,
  Settings,
  HelpCircleIcon,
  MessageCircle,
  MoreVertical,
  X,
} from "lucide-react";
import { driver } from "driver.js";
import startProfileTour from "@/pages/brand/onboarding/ProfileSetupTour";
import startAffiliatesManagementTour from "@/pages/brand/onboarding/AffiliatesManagementTour";
import startCampaignTour from "@/pages/brand/onboarding/CampaignTour";
import startSupportTour from "@/pages/brand/onboarding/SupportTour";
import startBrandGrandTour from "@/pages/brand/onboarding/BrandGrandTour";
import { useNavigate, useLocation } from "react-router-dom";
import { useOnboardingFloatingMenu } from "@/hooks/useOnboardingFloatingMenu";
import { useBrandGrandTour } from "@/hooks/useBrandGrandTour";
import { useBrandAuth } from "@/contexts/BrandAuthContext/context";
import startCurrentPageTour, {
  getCurrentPageLabel,
  hasCurrentPageTour,
} from "./page-guides";
import startAffiliatesPayoutTour from "./AffiliatesPayoutTour";
import startReveshareResourcesTour from "./ReveshareResourcesTour";
import { IconMoneybag } from "@tabler/icons-react";
import { BookOpen } from "lucide-react";

declare global {
  interface Window {
    jivo_api?: {
      open: () => void;
      close: () => void;
      setContactInfo: (info: {
        name?: string;
        email?: string;
        phone?: string;
        description?: string;
      }) => void;
    };
    // JivoChat global callback functions
    jivo_onOpen?: () => void;
    jivo_onClose?: () => void;
    jivo_onLoadCallback?: () => void;
  }
}

// Key to track if the user has seen the initial highlight
const JIVO_MENU_HIGHLIGHT_KEY = "reveshare_jivo_menu_highlight_shown";

interface OnboardingFloatingMenuProps {
  isVisible?: boolean;
}

export default function OnboardingFloatingMenu({
  isVisible: propIsVisible,
}: OnboardingFloatingMenuProps) {
  const { isVisible: hookIsVisible } = useOnboardingFloatingMenu();
  const { user } = useBrandAuth();
  const isVisible = propIsVisible ?? hookIsVisible;
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { shouldShowTour, hideTour } = useBrandGrandTour();
  const [currentPageLabel, setCurrentPageLabel] = useState<string>(() =>
    getCurrentPageLabel()
  );
  const [hasPageTour, setHasPageTour] = useState<boolean>(() =>
    hasCurrentPageTour()
  );
  const [isMoreToursExpanded, setIsMoreToursExpanded] = useState(false);
  // JivoChat state
  const [isJivoAvailable, setIsJivoAvailable] = useState(false);
  const [isJivoChatOpen, setIsJivoChatOpen] = useState(false);
  const hasShownHighlightRef = useRef(false);
  const highlightDriverRef = useRef<ReturnType<typeof driver> | null>(null);

  // Other tour items (not including about-this-page)
  const otherTourItems = [
    {
      id: "profile-tour",
      icon: Settings,
      label: "Profile Setup Tour",
      color: "bg-brand-600 hover:bg-brand-700",
      tour: "profile",
    },
    {
      id: "campaigns-tour",
      icon: Target,
      label: "Campaigns Management Tour",
      color: "bg-brand-500 hover:bg-brand-600",
      tour: "campaigns",
    },
    {
      id: "affiliates-tour",
      icon: Users,
      label: "Affiliates Management Tour",
      color: "bg-brand-500 hover:bg-brand-600",
      tour: "affiliates",
    },
    {
      id: "payouts-tour",
      icon: IconMoneybag,
      label: "Affiliates Payouts Tour",
      color: "bg-brand-500 hover:bg-brand-600",
      tour: "payouts",
    },
    {
      id: "reveshare-resources-tour",
      icon: BookOpen,
      label: "Reveshare Resources Tour",
      color: "bg-brand-600 hover:bg-brand-700",
      tour: "reveshare-resources",
    },
    {
      id: "support-tour",
      icon: MessageCircle,
      label: "Support & Help Tour",
      color: "bg-brand-400 hover:bg-brand-500",
      tour: "support",
    },
  ];

  // About this page item
  const aboutThisPageItem = {
    id: "about-this-page",
    icon: HelpCircleIcon,
    label: currentPageLabel,
    color: "bg-[#E879F9] hover:bg-[#D946EF]",
    tour: "current-page",
  };

  // Combined menu items for the radial menu (non-JivoChat)
  const menuItems = [...otherTourItems, aboutThisPageItem];

  // Update the current page label and tour availability when the route changes
  useEffect(() => {
    setCurrentPageLabel(getCurrentPageLabel());
    setHasPageTour(hasCurrentPageTour());
    setIsMoreToursExpanded(false); // Reset expanded state on route change
  }, [location.pathname, location.search]);

  // Auto-start grand tour if shouldShowTour is true
  useEffect(() => {
    if (shouldShowTour) {
      handleTourStart("grand");
      hideTour(); // Hide the tour after starting it
    }
  }, [shouldShowTour]);

  // Handle JivoChat opened event
  const handleJivoChatOpened = useCallback(() => {
    setIsJivoChatOpen(true);
    // Set user contact info in JivoChat when it opens
    if (user && window.jivo_api?.setContactInfo) {
      window.jivo_api.setContactInfo({
        name: user.name,
        email: user.email,
      });
    }
  }, [user]);

  // Handle JivoChat closed event
  const handleJivoChatClosed = useCallback(() => {
    setIsJivoChatOpen(false);
    // Dismiss any active highlight when chat closes
    if (highlightDriverRef.current) {
      highlightDriverRef.current.destroy();
      highlightDriverRef.current = null;
    }
  }, []);

  // Check for JivoChat availability and set up global callback functions
  useEffect(() => {
    // Store previous callbacks to chain them if they exist
    const previousOnOpen = window.jivo_onOpen;
    const previousOnClose = window.jivo_onClose;

    // Set up global callback for when chat opens
    window.jivo_onOpen = () => {
      // Call any previously defined callback
      if (previousOnOpen) previousOnOpen();
      handleJivoChatOpened();
    };

    // Set up global callback for when chat closes
    window.jivo_onClose = () => {
      // Call any previously defined callback
      if (previousOnClose) previousOnClose();
      handleJivoChatClosed();
    };

    // Check if JivoChat is already loaded
    const checkJivoAvailability = () => {
      if (window.jivo_api) {
        setIsJivoAvailable(true);
        // Set user contact info when JivoChat becomes available
        if (user && window.jivo_api.setContactInfo) {
          window.jivo_api.setContactInfo({
            name: user.name,
            email: user.email,
          });
        }
        return true;
      }
      return false;
    };

    // Check immediately
    const isAlreadyAvailable = checkJivoAvailability();

    // Set up a callback for when JivoChat loads
    window.jivo_onLoadCallback = () => {
      setIsJivoAvailable(true);
    };

    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    // Only poll if not already available
    if (!isAlreadyAvailable) {
      pollInterval = setInterval(() => {
        if (checkJivoAvailability()) {
          if (pollInterval) clearInterval(pollInterval);
        }
      }, 500);

      // Cleanup after 10 seconds if JivoChat never loads
      timeout = setTimeout(() => {
        if (pollInterval) clearInterval(pollInterval);
      }, 10000);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (timeout) clearTimeout(timeout);

      // Restore previous callbacks
      window.jivo_onOpen = previousOnOpen;
      window.jivo_onClose = previousOnClose;
    };
  }, [handleJivoChatOpened, handleJivoChatClosed, user]);

  // Show Driver.js highlight on initial load when JivoChat opens
  useEffect(() => {
    if (!isJivoChatOpen || !isVisible) return;

    // Check if we've already shown the highlight in this session
    const hasSeenHighlight = sessionStorage.getItem(JIVO_MENU_HIGHLIGHT_KEY);
    if (hasSeenHighlight || hasShownHighlightRef.current) return;

    // Mark as shown
    hasShownHighlightRef.current = true;
    sessionStorage.setItem(JIVO_MENU_HIGHLIGHT_KEY, "true");

    // Small delay to ensure the stacked menu is rendered
    const highlightTimeout = setTimeout(() => {
      const driverInstance = driver({
        showProgress: false,
        allowClose: true,
        stagePadding: 8,
        popoverClass: "jivo-menu-highlight-popover",
        steps: [
          {
            element: "#jivo-stacked-menu",
            popover: {
              title: "Need Help? 🎯",
              description:
                "Click on these options to get interactive tours and explanations for different features of the platform.",
              side: "left",
              align: "center",
            },
          },
        ],
        onDestroyed: () => {
          highlightDriverRef.current = null;
        },
      });

      highlightDriverRef.current = driverInstance;
      driverInstance.drive();
    }, 300);

    return () => {
      clearTimeout(highlightTimeout);
    };
  }, [isJivoChatOpen, isVisible]);

  const handleTourStart = (tourType: string) => {
    const driverInstance = driver();

    const onNextClickProfileTour = (element?: Element) => {
      const currentElement = element?.id;
      if (currentElement === "settings") {
        navigate("/brand/settings");
      }
      if (currentElement === "create-campaign") {
        navigate("/brand/affiliates/active");
      }
      if (currentElement === "my-details") {
        navigate("/brand/settings?tab=company-details");
      }
      if (currentElement === "company-details") {
        navigate("/brand/settings?tab=appearance-settings");
      }
      if (currentElement === "appearance-settings") {
        navigate("/brand/settings?tab=program-settings");
      }
      if (currentElement === "program-settings") {
        navigate("/brand/settings?tab=share-resources");
      }
      if (currentElement === "share-resources") {
        navigate("/brand/settings?tab=pricing");
      }
    };

    const onPrevClickProfileTour = (element?: Element) => {
      const previousElement = element?.id;
      if (previousElement === "pricing") {
        navigate("/brand/settings?tab=share-resources");
      }
      if (previousElement === "share-resources") {
        navigate("/brand/settings?tab=program-settings");
      }
      if (previousElement === "program-settings") {
        navigate("/brand/settings?tab=appearance-settings");
      }
      if (previousElement === "appearance-settings") {
        navigate("/brand/settings?tab=company-details");
      }
      if (previousElement === "company-details") {
        navigate("/brand/settings?tab=my-details");
      }
      if (previousElement === "my-details") {
        navigate("/brand/settings");
      }
      if (previousElement === "settings") {
        navigate("/brand/dashboard");
      }
    };

    const onNextClickAffiliateTour = (element?: Element) => {
      const currentElement = element?.id;
      if (currentElement === "affiliates") {
        navigate("/brand/affiliates");
      }
      if (currentElement === "invite-affiliates") {
        navigate("/brand/affiliates/invited");
      }
      if (currentElement === "invited") {
        navigate("/brand/affiliates/applicants");
      }
      if (currentElement === "applicants") {
        navigate("/brand/payouts");
      }
    };

    const onPrevClickAffiliateTour = (element?: Element) => {
      const previousElement = element?.id;
      if (previousElement === "invite-affiliates") {
        navigate("/brand/affiliates");
      }
      if (previousElement === "invited") {
        navigate("/brand/affiliates");
      }
      if (previousElement === "applicants") {
        navigate("/brand/affiliates/invited");
      }
      if (previousElement === "payouts") {
        navigate("/brand/affiliates/applicants");
      }
    };

    const onNextClickCampaignTour = (element?: Element) => {
      const currentElement = element?.id;
      if (currentElement === "campaigns") {
        navigate("/brand/campaigns?tab=active");
      }

      if (currentElement === "active-campaigns") {
        navigate("/brand/campaigns?tab=create");
      }
    };

    const onPrevClickCampaignTour = (element?: Element) => {
      const previousElement = element?.id;
      if (previousElement === "active-campaigns") {
        navigate("/brand/campaigns");
      }
      if (previousElement === "create-campaign") {
        navigate("/brand/campaigns?tab=active");
      }
      if (previousElement === "campaigns") {
        navigate("/brand/dashboard");
      }
    };

    const onNextClickSupportTour = (element?: Element) => {
      const currentElement = element?.id;
      if (currentElement === "support") {
        navigate("/brand/support");
      }
    };

    const onPrevClickSupportTour = (element?: Element) => {
      const previousElement = element?.id;
      if (previousElement === "support") {
        navigate("/brand/dashboard");
      }
    };

    // Grand tour navigation handlers
    const onNextClickGrandTour = (element?: Element) => {
      const currentElement = element?.id;

      if (currentElement === "tour-guide-button") {
        setIsHovered(true);
        setHoveredItem(5);
      }

      if (currentElement === "settings") {
        navigate("/brand/settings");
        setHoveredItem(null);
        setIsHovered(false);
      }
      if (currentElement === "my-details") {
        navigate("/brand/settings?tab=company-details");
      }
      if (currentElement === "company-details") {
        navigate("/brand/settings?tab=appearance-settings");
      }
      if (currentElement === "appearance-settings") {
        navigate("/brand/settings?tab=program-settings");
      }
      if (currentElement === "program-settings") {
        navigate("/brand/settings?tab=share-resources");
      }
      if (currentElement === "share-resources") {
        navigate("/brand/settings?tab=pricing");
      }

      // Campaign Tour navigation
      if (currentElement === "campaigns") {
        navigate("/brand/campaigns?tab=active");
      }
      if (currentElement === "active-campaigns") {
        navigate("/brand/campaigns?tab=create");
      }

      // Affiliates Management Tour navigation
      if (currentElement === "affiliates") {
        navigate("/brand/affiliates");
      }
      if (currentElement === "invite-affiliates") {
        navigate("/brand/affiliates/invited");
      }
      if (currentElement === "invited") {
        navigate("/brand/affiliates/applicants");
      }
      if (currentElement === "applicants") {
        navigate("/brand/payouts");
      }

      // Support Tour navigation
      if (currentElement === "support") {
        navigate("/brand/support");
      }
    };

    const onPrevClickGrandTour = (element?: Element) => {
      const previousElement = element?.id;

      if (previousElement === "settings") {
        setIsHovered(true);
        setHoveredItem(5);
      }

      if (previousElement === "tour-guide-button") {
        setIsHovered(false);
        setHoveredItem(null);
      }

      // Support Tour navigation
      if (previousElement === "support") {
        navigate("/brand/dashboard");
      }

      // Affiliates Management Tour navigation
      if (previousElement === "payouts") {
        navigate("/brand/affiliates/applicants");
      }
      if (previousElement === "applicants") {
        navigate("/brand/affiliates/invited");
      }
      if (previousElement === "invited") {
        navigate("/brand/affiliates");
      }
      if (previousElement === "invite-affiliates") {
        navigate("/brand/affiliates");
      }

      // Campaign Tour navigation
      if (previousElement === "create-campaign") {
        navigate("/brand/campaigns?tab=active");
      }
      if (previousElement === "active-campaigns") {
        navigate("/brand/campaigns");
      }
      if (previousElement === "campaigns") {
        navigate("/brand/dashboard");
      }

      // Profile Setup Tour navigation
      if (previousElement === "pricing") {
        navigate("/brand/settings?tab=share-resources");
      }
      if (previousElement === "share-resources") {
        navigate("/brand/settings?tab=program-settings");
      }
      if (previousElement === "program-settings") {
        navigate("/brand/settings?tab=appearance-settings");
      }
      if (previousElement === "appearance-settings") {
        navigate("/brand/settings?tab=company-details");
      }
      if (previousElement === "company-details") {
        navigate("/brand/settings?tab=my-details");
      }
      if (previousElement === "my-details") {
        navigate("/brand/settings");
      }
      if (previousElement === "settings") {
        navigate("/brand/dashboard");
      }
    };

    switch (tourType) {
      case "profile":
        startProfileTour(
          driverInstance,
          onNextClickProfileTour,
          onPrevClickProfileTour
        );
        break;
      case "affiliates":
        startAffiliatesManagementTour(
          driverInstance,
          onNextClickAffiliateTour,
          onPrevClickAffiliateTour
        );
        break;
      case "payouts":
        startAffiliatesPayoutTour(
          driverInstance,
          onNextClickAffiliateTour,
          onPrevClickAffiliateTour
        );
        break;
      case "reveshare-resources":
        startReveshareResourcesTour(
          driverInstance,
          onNextClickAffiliateTour,
          onPrevClickAffiliateTour
        );
        break;
      case "campaigns":
        startCampaignTour(
          driverInstance,
          onNextClickCampaignTour,
          onPrevClickCampaignTour
        );
        break;
      case "support":
        startSupportTour(
          driverInstance,
          onNextClickSupportTour,
          onPrevClickSupportTour
        );
        break;
      case "grand":
        startBrandGrandTour(
          driverInstance,
          onNextClickGrandTour,
          onPrevClickGrandTour
        );
        break;
      case "current-page":
        startCurrentPageTour(driverInstance, setCurrentPageLabel);
        break;
      default:
        break;
    }
    window.jivo_api?.close();
  };

  if (!isVisible) return null;

  // If JivoChat is available, show the stacked menu only when chat is open
  if (isJivoAvailable) {
    if (!isJivoChatOpen) {
      return null; // Hide menu when JivoChat is available but collapsed
    }

    // Render vertically stacked menu next to JivoChat
    return (
      <div
        id="jivo-stacked-menu"
        className="no-print fixed bottom-2 right-[380px] z-40 flex flex-col items-center gap-2"
        style={{
          animation: "slideInFromRight 0.3s ease-out",
        }}
      >
        <style>{`
          @keyframes slideInFromRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 0 8px 2px rgb(var(--brand-500) / 0.4), 0 4px 12px rgba(0, 0, 0, 0.15);
            }
            50% {
              box-shadow: 0 0 16px 4px rgb(var(--brand-500) / 0.6), 0 4px 16px rgba(0, 0, 0, 0.2);
            }
          }
          
          @keyframes color-shift {
            0%, 100% {
              background: linear-gradient(135deg, rgb(var(--brand-500)) 0%, rgb(var(--brand-600)) 100%);
            }
            50% {
              background: linear-gradient(135deg, rgb(var(--brand-400)) 0%, rgb(var(--brand-600)) 100%);
            }
          }
          
          .about-page-btn-animated {
            animation: pulse-glow 2s ease-in-out infinite, color-shift 3s ease-in-out infinite;
          }
          
          .jivo-menu-highlight-popover {
            background: linear-gradient(135deg, rgb(var(--brand-500)) 0%, rgb(var(--brand-700)) 100%) !important;
            color: white !important;
          }
          
          .jivo-menu-highlight-popover .driver-popover-title {
            color: white !important;
            font-weight: 600 !important;
          }
          
          .jivo-menu-highlight-popover .driver-popover-description {
            color: rgba(255, 255, 255, 0.9) !important;
          }
          
          .jivo-menu-highlight-popover button {
            background: white !important;
            color: rgb(var(--brand-600)) !important;
            border: none !important;
          }
          
          .jivo-menu-highlight-popover button:hover {
            background: rgba(255, 255, 255, 0.9) !important;
          }
        `}</style>

        {/* About This Page Button - only shown when page has a tour */}
        {hasPageTour && (
          <div
            className="relative group"
            style={{
              animation: "slideInFromRight 0.3s ease-out",
            }}
            onMouseEnter={() => setHoveredItem(-1)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <button
              id="stacked-about-this-page"
              onClick={() => handleTourStart("current-page")}
              className="about-page-btn-animated text-white p-3 rounded-full transition-all duration-200 hover:scale-110"
            >
              <HelpCircleIcon className="w-7 h-7" />
            </button>

            {/* Left-side tooltip for About This Page */}
            <div
              className={`absolute right-full top-1/2 -translate-y-1/2 mr-3 transition-all duration-200 pointer-events-none ${
                hoveredItem === -1
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-2"
              }`}
            >
              <div className="bg-gradient-to-r from-brand-500 to-brand-600 text-white px-3 py-2 rounded-lg text-sm shadow-lg w-[220px] text-right leading-snug">
                {currentPageLabel}
                <div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-brand-600" />
              </div>
            </div>
          </div>
        )}

        {/* More Tours Button - only shown when page has a tour */}
        {hasPageTour && (
          <div
            className="relative group"
            style={{
              animation: "slideInFromRight 0.3s ease-out 50ms both",
            }}
            onMouseEnter={() => setHoveredItem(-2)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <button
              id="stacked-more-tours"
              onClick={() => setIsMoreToursExpanded(!isMoreToursExpanded)}
              className={`${
                isMoreToursExpanded
                  ? "bg-gray-600 hover:bg-gray-700"
                  : "bg-brand-600 hover:bg-brand-700"
              } text-white p-2 rounded-full shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg`}
            >
              {isMoreToursExpanded ? (
                <X className="w-5 h-5" />
              ) : (
                <MoreVertical className="w-5 h-5" />
              )}
            </button>

            {/* Left-side tooltip for More Tours */}
            <div
              className={`absolute right-full top-1/2 -translate-y-1/2 mr-3 transition-all duration-200 pointer-events-none ${
                hoveredItem === -2
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-2"
              }`}
            >
              <div className="bg-brand-600 text-white px-3 py-1.5 rounded-lg text-sm whitespace-nowrap shadow-lg">
                {isMoreToursExpanded ? "Close" : "Show more tours"}
                <div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-brand-600" />
              </div>
            </div>
          </div>
        )}

        {/* Expanded Tour Items - shown when expanded OR when page doesn't have a tour */}
        {(isMoreToursExpanded || !hasPageTour) &&
          otherTourItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="relative group"
                style={{
                  animation: `slideInFromRight 0.3s ease-out ${
                    (index + 1) * 50
                  }ms both`,
                }}
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <button
                  id={`stacked-${item.id}`}
                  onClick={() => handleTourStart(item.tour)}
                  className={`${item.color} text-white p-1.5 rounded-full shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg`}
                >
                  <Icon className="w-5 h-5" />
                </button>

                {/* Left-side tooltip */}
                <div
                  className={`absolute right-full top-1/2 -translate-y-1/2 mr-3 transition-all duration-200 pointer-events-none ${
                    hoveredItem === index
                      ? "opacity-100 translate-x-0"
                      : "opacity-0 translate-x-2"
                  }`}
                >
                  <div className="bg-brand-600 text-white px-3 py-1.5 rounded-lg text-sm whitespace-nowrap shadow-lg">
                    {item.label}
                    <div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-brand-600" />
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    );
  }

  // Default radial floating menu (when JivoChat is not available)
  return (
    <div
      className="no-print fixed bottom-6 right-6 z-50"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Invisible hover area to keep menu open */}
      <div
        className={`absolute transition-all duration-300 ${
          isHovered
            ? "opacity-0 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        style={{
          width: "300px",
          height: "300px",
          top: "-160px",
          left: "-160px",
          borderRadius: "50%",
        }}
      />

      {/* Menu Items */}
      {menuItems.map((item, index) => {
        const Icon = item.icon;
        const angle = 180 + index * 18; // Distribute from left (180°) to top (270°)
        const radius = 150; // Increased radius for more space between icons and tooltips
        const x = Math.cos((angle * Math.PI) / 180) * radius;
        const y = Math.sin((angle * Math.PI) / 180) * radius;

        return (
          <div
            key={index}
            className={`absolute transition-all duration-300 ease-out ${
              isHovered
                ? "opacity-100 scale-100 pointer-events-auto"
                : "opacity-0 scale-75 pointer-events-none"
            }`}
            style={{
              transform: `translate(${x}px, ${y}px)`,
              transitionDelay: `${index * 50}ms`,
            }}
            onMouseEnter={() => setHoveredItem(index)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div id={item.id} className="relative group">
              <button
                onClick={() => handleTourStart(item.tour)}
                className={`${item.color} text-white p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110 backdrop-blur-sm bg-opacity-90`}
              >
                <Icon className="w-5 h-5" />
              </button>

              {/* Label */}
              <div
                id={item.id}
                className={`absolute transition-all duration-200 ${
                  index === menuItems.length - 1
                    ? `bottom-full left-1/6 -translate-x-1/2 mb-2 ${
                        hoveredItem === index
                          ? "opacity-100 -translate-y-2"
                          : "opacity-0 -translate-y-2"
                      }`
                    : `right-full top-1/2 -translate-y-1/2 mr-4 ${
                        hoveredItem === index
                          ? "opacity-100 translate-x-0"
                          : "opacity-0 translate-x-2"
                      }`
                }`}
              >
                <div className="bg-brand-600 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap shadow-lg backdrop-blur-sm bg-opacity-90">
                  {item.label}
                  <div
                    className={`absolute ${
                      index === menuItems.length - 1
                        ? "top-full right-1/4 -translate-x-1/2 border-4 border-transparent border-t-brand-600"
                        : "left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-brand-600"
                    }`}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Main Button */}
      <div className="relative">
        <button
          id="tour-guide-button"
          className={`bg-brand-600 hover:bg-brand-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 backdrop-blur-sm bg-opacity-90 ${
            isHovered ? "rotate-45 scale-110" : "rotate-0 scale-100"
          }`}
        >
          <Compass className="w-5 h-5" />
        </button>

        {/* Main button tooltip */}
        <div
          className={`absolute bottom-full left-1/2 -translate-x-1/2 mt-3 transition-all duration-200 ${
            isHovered
              ? "opacity-100 -translate-y-2"
              : "opacity-0 -translate-y-2"
          }`}
        >
          <div className="bg-brand-600 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap shadow-lg backdrop-blur-sm bg-opacity-90">
            Take a tour
          </div>
        </div>
      </div>

      {/* Backdrop blur effect */}
      <div
        className={`absolute inset-0 rounded-full transition-all duration-300 ${
          isHovered
            ? "bg-black bg-opacity-5 scale-150"
            : "bg-transparent scale-100"
        }`}
        style={{ zIndex: -1 }}
      />
    </div>
  );
}
